/**
 * contact-context.service.ts
 *
 * Helpers purs (zero side-effect) qui transforment les donnees brutes
 * du contact en libelles humains pour l'UI.
 *
 *  - buildProjectSummary : phrase courte resumant le projet immo
 *  - recommendNextAction : action prioritaire pour l'agent
 *  - formatStageCampaignLabel : libelle d'etape de campagne
 */

import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import type { ContactRow } from "@/hooks/use-contacts";
import type { ContactActiveEnrollment } from "@/hooks/use-sequences";
import { getEntryCampaignLabel } from "@/lib/services/heat-score.service";

const PROJECT_TYPE_SHORT: Record<string, string> = {
  achat_rp: "Achat RP",
  achat_investissement: "Achat invest.",
  vente: "Vente",
  location: "Location",
  estimation: "Estimation",
  indefini: "Projet indefini",
};

export const PROJECT_TIMELINE_SHORT: Record<string, string> = {
  immediat: "immediat",
  "3_mois": "3 mois",
  "6_mois": "6 mois",
  "1_an": "1 an",
  indefini: "horizon flou",
};

export function formatBudgetRange(min: number | null, max: number | null): string | null {
  if (!min && !max) return null;
  const fmt = (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k€` : `${v}€`);
  if (min && max) return `${fmt(min)}-${fmt(max)}`;
  if (max) return `≤ ${fmt(max)}`;
  if (min) return `≥ ${fmt(min!)}`;
  return null;
}

// ─── Resume projet ────────────────────────────────────────────────────────

export type ProjectStatus = "qualified" | "partial" | "empty";

export interface ProjectSummary {
  label: string;
  status: ProjectStatus;
  missing: string[];
}

export function buildProjectSummary(contact: ContactRow): ProjectSummary {
  // Le project_type ("Achat RP", "Vente"...) est redondant avec la colonne Profil
  // (un acheteur fait de l'achat, un vendeur de la vente). On le garde pour le
  // calcul `missing` mais on ne l'affiche plus dans le label.
  const hasType = !!contact.project_type && contact.project_type !== "indefini";
  const hasCity = !!contact.project_city;
  const hasBudget = !!(contact.budget_min || contact.budget_max);
  const hasTimeline =
    !!contact.project_timeline && contact.project_timeline !== "indefini";

  const visibleCount = [hasCity, hasBudget, hasTimeline].filter(Boolean).length;

  if (visibleCount === 0) {
    return { label: "Projet à qualifier", status: "empty", missing: [] };
  }

  const parts: string[] = [];
  if (hasCity) parts.push(contact.project_city!);
  if (hasBudget) {
    const range = formatBudgetRange(contact.budget_min, contact.budget_max);
    if (range) parts.push(range);
  }
  if (hasTimeline)
    parts.push(PROJECT_TIMELINE_SHORT[contact.project_timeline!] ?? contact.project_timeline!);

  const missing: string[] = [];
  if (!hasType) missing.push("type de bien");
  if (!hasCity) missing.push("ville");
  if (!hasBudget) missing.push("budget");
  if (!hasTimeline) missing.push("horizon");

  // Jaune assoupli : >= 2 champs visibles (city / budget / timeline) suffisent.
  // 1 seul champ → partial (jaune discret).
  const status: ProjectStatus = visibleCount >= 2 ? "qualified" : "partial";

  return {
    label: parts.join(" · "),
    status,
    missing,
  };
}

// ─── Etape campagne ───────────────────────────────────────────────────────

export interface CampaignStageLabel {
  label: string;
  tone: "active" | "paused" | "completed" | "none";
}

export function formatStageCampaignLabel(
  enrollment: ContactActiveEnrollment | undefined
): CampaignStageLabel {
  if (!enrollment) return { label: "Non inscrit", tone: "none" };
  const { currentStep, totalSteps, status } = enrollment;

  if (status === "completed" || (totalSteps > 0 && currentStep >= totalSteps)) {
    return { label: "Séquence terminée", tone: "completed" };
  }
  if (status === "paused") {
    return { label: `Pause · ${currentStep}/${totalSteps}`, tone: "paused" };
  }
  if (currentStep === 0) {
    return { label: `Email 1/${totalSteps} à venir`, tone: "active" };
  }
  return { label: `Email ${currentStep}/${totalSteps} envoyé`, tone: "active" };
}

// ─── Action recommandee ───────────────────────────────────────────────────

export type ActionPriority = "high" | "medium" | "low";

export interface NextAction {
  label: string;
  priority: ActionPriority;
}

export interface LastExchangeSummary {
  direction: "in" | "out";
  sentAt: string;
}

export function recommendNextAction(
  contact: ContactRow,
  enrollment: ContactActiveEnrollment | undefined,
  lastExchange: LastExchangeSummary | undefined
): NextAction {
  // 1. Reponse entrante recente (< 48h) : priorite absolue
  if (lastExchange?.direction === "in") {
    const ageHours =
      (Date.now() - new Date(lastExchange.sentAt).getTime()) / (1000 * 60 * 60);
    if (ageHours < 48) return { label: "Répondre", priority: "high" };
  }

  // 2. Chaud sans RDV ni mandat : appeler
  if (
    contact.heat_score === "hot" &&
    contact.stage !== "rdv" &&
    contact.stage !== "mandate"
  ) {
    return { label: "Appeler", priority: "high" };
  }

  // 3. Mandat signe sans cles delivrees : suivre
  if (contact.stage === "mandate" && !contact.keys_delivered_at) {
    return { label: "Suivre transaction", priority: "medium" };
  }

  // 4. Pas d'enrollment : qualifier ou inscrire
  if (!enrollment) {
    const project = buildProjectSummary(contact);
    if (project.status === "empty") {
      return { label: "Qualifier le projet", priority: "medium" };
    }
    return { label: "Inscrire en séquence", priority: "medium" };
  }

  // 5. Enrollment en pause
  if (enrollment.status === "paused") {
    return { label: "Réactiver séquence", priority: "low" };
  }

  // 6. Enrollment termine
  if (enrollment.status === "completed") {
    return { label: "Relancer", priority: "medium" };
  }

  // 7. Default : laisser couler la sequence
  return { label: "Attendre réponse", priority: "low" };
}

// ─── Profil (libelle court) ───────────────────────────────────────────────

const PROFILE_SHORT: Record<string, string> = {
  acheteur: "Acheteur",
  vendeur: "Vendeur",
  bailleur: "Bailleur",
  locataire: "Locataire",
  pro_mdb: "Pro MDB",
  pro_investisseur: "Investisseur",
  ambassadeur: "Ambassadeur",
  indefini: "A qualifier",
};

export function getProfileLabel(role: string | null | undefined): string {
  if (!role) return PROFILE_SHORT.indefini;
  return PROFILE_SHORT[role] ?? role;
}

// ─── Resume contexte (phrase narrative) ───────────────────────────────────

const PROFILE_NARRATIVE: Record<string, string> = {
  acheteur: "acheteur",
  vendeur: "vendeur",
  bailleur: "bailleur",
  locataire: "locataire",
  pro_mdb: "professionnel MDB",
  pro_investisseur: "investisseur",
  ambassadeur: "ambassadeur",
  indefini: "contact a qualifier",
};

const HEAT_NARRATIVE: Record<string, string> = {
  hot: "chaud",
  warm: "tiède",
  cold: "froid",
};

function buildIdentitySegment(contact: ContactRow): string {
  const firstName = contact.first_name ?? contact.name?.split(" ")[0] ?? "Ce contact";
  const profile = PROFILE_NARRATIVE[contact.contact_role ?? "indefini"] ?? "contact";
  const heat = HEAT_NARRATIVE[contact.heat_score ?? "cold"] ?? "froid";
  const origin = contact.entry_campaign
    ? getEntryCampaignLabel(contact.entry_campaign).label
    : null;
  if (origin) {
    return `${firstName} est ${profile} ${heat} entre via ${origin}.`;
  }
  return `${firstName} est ${profile} ${heat}.`;
}

export function buildEnrollmentSegment(enrollment: ContactActiveEnrollment | undefined): string {
  if (!enrollment) return "Pas encore inscrit en campagne.";
  const { sequenceName, currentStep, totalSteps, status } = enrollment;
  if (status === "completed" || (totalSteps > 0 && currentStep >= totalSteps)) {
    return `A termine la sequence ${sequenceName}.`;
  }
  if (status === "paused") {
    return `Inscrit dans ${sequenceName} (en pause).`;
  }
  if (currentStep === 0) {
    return `Inscrit dans ${sequenceName}, premier email a venir.`;
  }
  return `A recu l'email ${currentStep} sur ${totalSteps} de la sequence ${sequenceName}.`;
}

function buildProjectSegment(project: ProjectSummary): string {
  if (project.status === "empty") return "Son projet reste a qualifier.";
  if (project.status === "partial") {
    if (project.missing.length === 0) return `Projet : ${project.label}.`;
    return `Projet incomplet : manque ${project.missing.join(", ")}.`;
  }
  return `Projet : ${project.label}.`;
}

export function buildExchangeSegment(lastExchange: LastExchangeSummary | undefined): string {
  if (!lastExchange) return "Aucun échange récent.";
  const ageMs = Date.now() - new Date(lastExchange.sentAt).getTime();
  const ageHours = ageMs / (1000 * 60 * 60);
  const distance = formatDistanceToNow(new Date(lastExchange.sentAt), {
    addSuffix: false,
    locale: fr,
  });
  if (ageHours < 48) {
    return lastExchange.direction === "in"
      ? `A repondu il y a ${distance}.`
      : `Dernier message envoye il y a ${distance}.`;
  }
  return lastExchange.direction === "in"
    ? `Dernier message recu il y a ${distance}.`
    : `Dernier echange il y a ${distance}.`;
}

export function buildContextSummary(
  contact: ContactRow,
  enrollment: ContactActiveEnrollment | undefined,
  lastExchange: LastExchangeSummary | undefined,
  action?: NextAction
): string {
  const project = buildProjectSummary(contact);
  const segments = [
    buildIdentitySegment(contact),
    buildEnrollmentSegment(enrollment),
    buildProjectSegment(project),
    buildExchangeSegment(lastExchange),
  ];
  // La reco ne fait partie du recit que si on la demande explicitement
  // (action fourni). Dans la fiche, la reco vit dans la carte « Juliette
  // recommande » -> selectContactSummary l'omet pour eviter le doublon.
  if (action) segments.push(`Prochaine action recommandee : ${action.label}.`);
  return segments.join(" ");
}
